import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ComplianceOverviewCard from '../components/compliance/ComplianceOverviewCard';
import ComplianceCheckCard from '../components/compliance/ComplianceCheckCard';
import usePageMetadata from '../utils/usePageMetadata';
import './Compliance.css';

const STATUS_WEIGHT = {
  pass: 1,
  warning: 0.5,
  fail: 0,
};

const scoreChecks = (checks) => {
  const totalChecks = checks.length;
  if (totalChecks === 0) {
    return {
      score: 0,
      statusCounts: { pass: 0, warning: 0, fail: 0 },
      overallStatus: 'fail',
    };
  }

  const statusCounts = checks.reduce(
    (accumulator, check) => {
      accumulator[check.status] += 1;
      return accumulator;
    },
    { pass: 0, warning: 0, fail: 0 }
  );

  const rawScore = checks.reduce((total, check) => {
    return total + STATUS_WEIGHT[check.status] * (100 / totalChecks);
  }, 0);

  const score = Math.round(rawScore);
  const overallStatus =
    statusCounts.fail > 0 ? 'fail' : statusCounts.warning > 0 ? 'warning' : 'pass';

  return { score, statusCounts, overallStatus };
};

const CompliancePublic = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [scanStatus, setScanStatus] = useState('idle');
  const [reportScore, setReportScore] = useState(null);
  const [reportStatusCounts, setReportStatusCounts] = useState(null);
  const [startedAt, setStartedAt] = useState(null);

  // Redirect logged-in users to the main compliance page
  useEffect(() => {
    if (token) {
      navigate('/compliance');
    }
  }, [token, navigate]);

  usePageMetadata({
    title: 'Free Website Compliance Checker | Ad Policy & Privacy Compliance Scanner',
    description:
      'Free compliance scanner for ads.txt, robots.txt, privacy policies, CMP detection, HTTPS validation, and ad tag security. Check your website\'s compliance with ad policy and privacy requirements instantly.',
    canonicalUrl: 'https://theadshop.co/compliance-checker',
    meta: [
      { property: 'og:title', content: 'Free Website Compliance Checker | Ad Policy & Privacy Compliance Scanner' },
      {
        property: 'og:description',
        content:
          'Free compliance scanner for ads.txt, robots.txt, privacy policies, CMP detection, HTTPS validation, and ad tag security.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/compliance-checker' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:title', content: 'Free Website Compliance Checker | Ad Policy & Privacy Compliance Scanner' },
      {
        name: 'twitter:description',
        content:
          'Free compliance scanner for ads.txt, robots.txt, privacy policies, CMP detection, HTTPS validation, and ad tag security.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Website Compliance Checker',
        description: 'Free compliance scanner for ads.txt, robots.txt, privacy policies, CMP detection, HTTPS validation, and ad tag security.',
        url: 'https://theadshop.co/compliance-checker',
        applicationCategory: 'WebApplication',
        operatingSystem: 'Any',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        featureList: [
          'ads.txt validation',
          'HTTPS & SSL validation',
          'CMP (Consent Management Platform) detection',
          'Privacy policy detection',
          'Ad tag security review',
          'robots.txt analysis',
          'Sitemap coverage scan',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'The Ad Shop Compliance Checker',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
      },
    ],
  });

  const handleScan = async () => {
    if (!url.trim()) {
      setError('Please enter a URL to scan');
      return;
    }

    setLoading(true);
    setError('');
    setScanStatus('running');
    setStartedAt(new Date().toISOString());
    setChecks([]);
    setReportScore(null);
    setReportStatusCounts(null);

    try {
      // Normalize URL
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      const response = await axios.post('/api/compliance/public', {
        url: normalizedUrl,
      });

      const data = response.data || {};
      if (Array.isArray(data.checks) && data.checks.length > 0) {
        setChecks(data.checks);
      }
      if (data.generatedAt) {
        setGeneratedAt(data.generatedAt);
      }
      setScanStatus(data.status || 'completed');
      setReportScore(typeof data.score === 'number' ? data.score : null);
      setReportStatusCounts(
        data.statusCounts && typeof data.statusCounts === 'object' ? data.statusCounts : null
      );
      setStartedAt(data.startedAt || null);

      if (data.status === 'error') {
        setError(data.errorMessage || 'Compliance scan failed. Please try again.');
      }
    } catch (err) {
      console.error('Error running compliance scan:', err);
      setError(
        err.response?.data?.message || 'Failed to run compliance scan. Please check the URL and try again.'
      );
      setScanStatus('error');
      setChecks([]);
      setGeneratedAt(null);
      setReportScore(null);
      setReportStatusCounts(null);
      setStartedAt(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleScan();
    }
  };

  const fallbackMetrics = useMemo(() => scoreChecks(checks), [checks]);

  const fallbackStatusCounts = fallbackMetrics.statusCounts;
  const effectiveScore = reportScore ?? fallbackMetrics.score;
  const effectiveStatusCounts = useMemo(
    () => ({
      pass: reportStatusCounts?.pass ?? fallbackStatusCounts.pass,
      warning: reportStatusCounts?.warning ?? fallbackStatusCounts.warning,
      fail: reportStatusCounts?.fail ?? fallbackStatusCounts.fail,
    }),
    [reportStatusCounts, fallbackStatusCounts]
  );
  const effectiveOverallStatus =
    effectiveStatusCounts.fail > 0
      ? 'fail'
      : effectiveStatusCounts.warning > 0
      ? 'warning'
      : 'pass';

  const noReportData = !checks.length && scanStatus !== 'running';
  const issuesNeedingAttention =
    (effectiveStatusCounts.fail || 0) + (effectiveStatusCounts.warning || 0);
  let highlight;
  if (scanStatus === 'running') {
    highlight = "Compliance scan in progress. We'll update results as each check finishes.";
  } else if (scanStatus === 'error') {
    highlight = 'We could not complete the last scan. Try running it again.';
  } else if (noReportData) {
    highlight = 'Enter a website URL above to run a free compliance scan and see results.';
  } else if (issuesNeedingAttention === 0) {
    highlight = 'Everything looks great. Keep monitoring for new changes.';
  } else {
    highlight = `Your site is ${effectiveScore}% compliant. ${issuesNeedingAttention} issue${
      issuesNeedingAttention > 1 ? 's' : ''
    } need attention.`;
  }

  return (
    <div className="compliance-page">
      <main className="compliance-content">
        <div className="compliance-header">
          <div className="header-copy">
            <h1>Free Website Compliance Checker</h1>
            <p>
              Check your website's compliance with ad policy and privacy requirements. Scan for ads.txt, robots.txt, privacy policies, CMP detection, HTTPS validation, and ad tag security issues.
            </p>
          </div>
          <div className="site-selector">
            <label htmlFor="url-input">Website URL</label>
            <div className="site-selector-controls">
              <input
                id="url-input"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="https://example.com"
                disabled={loading}
                className="url-input"
              />
              <button
                type="button"
                className="refresh-report-btn"
                onClick={handleScan}
                disabled={loading || !url.trim()}
              >
                {loading ? 'Scanning...' : 'Scan Website'}
              </button>
            </div>
            {generatedAt && (
              <p className="generated-at">Report generated {new Date(generatedAt).toLocaleString()}</p>
            )}
            {scanStatus === 'running' && (
              <div className="scan-progress">
                <div className="scan-progress-header">
                  <span className="scan-progress-indicator" aria-hidden="true" />
                  <span>Compliance scan in progress</span>
                </div>
                {startedAt && (
                  <p className="scan-progress-started">
                    Started {new Date(startedAt).toLocaleString()}
                  </p>
                )}
                <p className="scan-progress-message">Running compliance checks...</p>
              </div>
            )}
            {scanStatus === 'error' && (
              <div className="scan-progress scan-progress-error">
                <div className="scan-progress-header">
                  <span className="scan-progress-indicator error" aria-hidden="true" />
                  <span>Compliance scan failed</span>
                </div>
                <p className="scan-progress-message">
                  {error || 'We could not finish the compliance scan. Try again soon.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {error && scanStatus !== 'error' && <div className="compliance-error">{error}</div>}

        {checks.length > 0 && (
          <>
            <ComplianceOverviewCard
              score={effectiveScore}
              totalChecks={checks.length}
              statusCounts={effectiveStatusCounts}
              highlight={highlight}
              breakdown={{ overallStatus: effectiveOverallStatus }}
            />

            <section className="compliance-cards">
              {checks.map((check) => (
                <ComplianceCheckCard key={check.id} check={check} />
              ))}
            </section>
          </>
        )}

        {noReportData && scanStatus !== 'running' && (
          <div className="compliance-results-empty">
            <h3>Ready to scan your website?</h3>
            <p>Enter a website URL above and click "Scan Website" to check compliance with ad policy and privacy requirements.</p>
            <div className="compliance-features-list">
              <h4>What we check:</h4>
              <ul>
                <li><strong>ads.txt Validation</strong> - Ensures your ads.txt file is properly formatted and accessible</li>
                <li><strong>HTTPS & SSL Validation</strong> - Verifies your site uses secure HTTPS connections</li>
                <li><strong>CMP Detection</strong> - Checks for Consent Management Platform implementation</li>
                <li><strong>Privacy Policy Detection</strong> - Verifies presence of privacy policy</li>
                <li><strong>Ad Tag Security</strong> - Reviews ad tags for security issues</li>
                <li><strong>robots.txt Analysis</strong> - Validates robots.txt configuration</li>
                <li><strong>Sitemap Coverage</strong> - Checks sitemap availability and references</li>
              </ul>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompliancePublic;

